import { classList } from '@findigs/helpers/src/helpers/class-list';
import { polymorphic } from '@findigs/helpers/src/helpers/polymorphic';
import type { PolymorphicHTMLProps } from '@findigs/helpers/src/helpers/polymorphic';
import type { JSX } from 'react';
import { Link } from '../link';
import { styles } from './form-alert.module.scss';
import SuccessIcon from '../../icons/material/check_circle.round.inline.svg';
import ErrorIcon from '../../icons/material/error.filled.inline.svg';
import InfoIcon from '../../icons/material/info.filled.inline.svg';
import WarningIcon from '../../icons/material/warning.round.inline.svg';

export const FORM_ALERT_VARIANT = [
  'Info',
  'Success',
  'Warning',
  'Error',
] as const;

export const FORM_ALERT_SIZE = ['Default', 'Small'] as const;

export const DEFAULT_FORM_ALERT_ERROR_MESSAGE =
  'An error occurred and we were unable to save this information. Please try again or contact us for help.';

export type FormAlertVariant = (typeof FORM_ALERT_VARIANT)[number];

export type FormAlertSize = (typeof FORM_ALERT_SIZE)[number];

export type FormAlertAction = {
  label: string;
  href?: string;
  target?: '_blank' | AnyOtherString;
  rel?: 'noopener noreferrer' | AnyOtherString;
  onClick?: () => void;
};

export type FormAlertProps = Omit<PolymorphicHTMLProps, 'size'> & {
  variant: FormAlertVariant;
  action?: FormAlertAction;
  /** @default 'Default' */
  size?: FormAlertSize;
};

const Icons: { [key in FormAlertVariant]: () => JSX.Element } = {
  Info: () => <InfoIcon width={24} height={24} aria-label="Info:" />,
  Success: () => <SuccessIcon width={24} height={24} aria-label="Success:" />,
  Warning: () => <WarningIcon width={24} height={24} aria-label="Warning:" />,
  Error: () => <ErrorIcon width={24} height={24} aria-label="Error:" />,
};

/**
 * Extends the vanilla `div` element with our styling.
 * All vanilla `div` props are forwarded.
 *
 * This component is [polymorphic](/docs/component-system-polymorphic-components--docs).
 */
export const FormAlert = polymorphic(
  { element: 'div', forwardRef: true },
  (
    {
      variant,
      action,
      className,
      children,
      size = 'Default',
      ...props
    }: FormAlertProps,
    Component,
    ref
  ) => {
    const classes = classList(styles, variant, `size-${size}`, className);
    return (
      <Component className={classes} {...props} ref={ref}>
        {Icons[variant]()}
        <div className="content">
          {typeof children === 'string' ? <p>{children}</p> : children}
          {action &&
            (action.href ? (
              <Link
                className="action"
                inverse
                href={action.href}
                target={action.target}
                rel={action.rel}
                onClick={action.onClick}
              >
                {action.label}
              </Link>
            ) : (
              <Link
                as="button"
                type="button"
                className="action"
                inverse
                onClick={action.onClick}
              >
                {action.label}
              </Link>
            ))}
        </div>
      </Component>
    );
  }
);
